'use client';

import { useRef, useEffect } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useTranslation } from "@/hooks/useTranslation";
import { motion } from 'framer-motion';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Smile, Users, Clock, Shield } from 'lucide-react';

gsap.registerPlugin(ScrollTrigger);

export default function WhatIsAllOnFourSection() {
    const { t } = useTranslation("all-on-4");
    const sectionRef = useRef<HTMLElement>(null);
    const titleRef = useRef<HTMLHeadingElement>(null);
    const contentRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const ctx = gsap.context(() => {
            gsap.fromTo(titleRef.current,
                { opacity: 0, y: 30 },
                {
                    opacity: 1,
                    y: 0,
                    duration: 0.8,
                    scrollTrigger: {
                        trigger: titleRef.current,
                        start: "top 80%",
                        toggleActions: "play none none reverse"
                    }
                }
            );

            if (contentRef.current?.children) {
                gsap.fromTo(contentRef.current.children,
                    { opacity: 0, y: 40 },
                    {
                        opacity: 1,
                        y: 0,
                        duration: 0.6,
                        stagger: 0.2,
                        scrollTrigger: {
                            trigger: contentRef.current,
                            start: "top 75%",
                            toggleActions: "play none none reverse"
                        }
                    }
                );
            }
        }, sectionRef);

        return () => ctx.revert();
    }, []);

    const benefits = [
        {
            icon: Clock,
            title: t("whatIs.benefits.sameDayResults.title"),
            description: t("whatIs.benefits.sameDayResults.description")
        },
        {
            icon: Shield,
            title: t("whatIs.benefits.successRate.title"),
            description: t("whatIs.benefits.successRate.description")
        },
        {
            icon: Users,
            title: t("whatIs.benefits.expertTeam.title"),
            description: t("whatIs.benefits.expertTeam.description")
        },
        {
            icon: Smile,
            title: t("whatIs.benefits.naturalLook.title"),
            description: t("whatIs.benefits.naturalLook.description")
        }
    ];

    return (
        <section
            id="what-is"
            ref={sectionRef}
            className="py-16 lg:py-24 bg-white dark:bg-slate-800 min-h-[700px]"
        >
            <div className="container">
                <div className="text-center mb-16">
                    <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-navy dark:text-white font-serif leading-tight mb-6">
                        {t("whatIs.title").includes("All-On-4") ? (
                            <>
                                {t("whatIs.title").split("All-On-4")[0]}
                                <span className="text-gold">All-On-4</span>
                                {t("whatIs.title").split("All-On-4")[1]}
                            </>
                        ) : t("whatIs.title")}
                    </h2>
                    <p className="text-lg text-slate-600 dark:text-slate-300 max-w-3xl mx-auto">
                        {t("whatIs.subtitle")}
                    </p>
                </div>
                <div className="grid lg:grid-cols-2 gap-12 items-center">
                    {/* Left Content — centered on mobile, left-aligned on lg+. */}
                    <div className="space-y-6 text-center lg:text-left">
                        <h2
                            ref={titleRef}
                            className="text-3xl md:text-4xl lg:text-5xl font-bold text-navy dark:text-white font-serif leading-tight"
                        >
                            {t("whatIs.mainTitle").includes("All-On-4") ? (
                                <>
                                    {t("whatIs.mainTitle").split("All-On-4")[0]}
                                    <span className="text-gold">All-On-4</span>
                                    {t("whatIs.mainTitle").split("All-On-4")[1]}
                                </>
                            ) : t("whatIs.mainTitle")}
                        </h2>

                        <div className="space-y-4 text-slate-600 dark:text-slate-300 text-lg leading-relaxed">
                            <p>
                                {t("whatIs.description1")}
                            </p>

                            <p>
                                {t("whatIs.description2")}
                            </p>
                        </div>

                        <div className="pt-4 flex justify-center lg:justify-start">
                            <Button
                                className="bg-gold hover:bg-gold-600 text-navy font-semibold px-6 py-3"
                                onClick={() => {
                                    const timelineSection = document.getElementById('timeline');
                                    timelineSection?.scrollIntoView({ behavior: 'smooth' });
                                }}
                            >
                                {t("whatIs.button")}
                            </Button>
                        </div>
                    </div>

                    {/* Right Content - Benefits Grid */}
                    <div ref={contentRef} className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                        {benefits.map((benefit, index) => (
                            <motion.div
                                key={index}
                                initial={{ opacity: 0, scale: 0.9 }}
                                whileInView={{ opacity: 1, scale: 1 }}
                                transition={{ delay: index * 0.1, duration: 0.5 }}
                                viewport={{ once: true }}
                            >
                                <Card className="h-full border-slate-200 dark:border-slate-700 hover:shadow-lg transition-shadow duration-300">
                                    <CardContent className="p-6 text-center">
                                        <div className="w-12 h-12 bg-gold/20 rounded-lg flex items-center justify-center mx-auto mb-4">
                                            <benefit.icon className="w-6 h-6 text-gold" />
                                        </div>
                                        <h3 className="text-lg font-semibold text-navy dark:text-white mb-2">
                                            {benefit.title}
                                        </h3>
                                        <p className="text-slate-600 dark:text-slate-300 text-sm">
                                            {benefit.description}
                                        </p>
                                    </CardContent>
                                </Card>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
} 